import React, { Fragment, cloneElement, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import type { SVGProps } from "react";
import { Modal } from "../../../components/common/Modal";
import OnboardingQuestions, {
  OnboardInfo,
} from "./../onboarding/OnboardingQuestions";
import { useAuth, useUser } from "@clerk/clerk-react";
import { updateUserMetadata } from "../../../methods/clerk";
import { ClerkMetadataKeys } from "@neum/frontend-shared/constants/clerk";
interface Step {
  icon: React.ReactElement<SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
}
import { SPECIALTIES } from "@neum/frontend-shared/constants/doctorData";
import VetRecButton from "../../common/VetRecButton";

interface WelcomeModalProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  startTour: () => void;
  onSkip: () => void;
  icon: React.ReactElement<SVGProps<SVGSVGElement>>;
  title: React.ReactNode;
  description: string;
  steps?: Step[];
  primaryButtonText?: string;
  skipButtonText?: string;
}

// welcome screen used to setup tour for different app features
const WelcomeScreen = ({
  isOpen,
  setIsOpen,
  startTour,
  onSkip,
  icon,
  title,
  description,
  steps,
  primaryButtonText = "Start Quick Tour",
  skipButtonText = "Skip",
}: WelcomeModalProps) => {
  // setup onboarding info if necessary
  const { getToken } = useAuth();
  const { user } = useUser();
  const getOnBoardInfo =
    user?.publicMetadata[ClerkMetadataKeys.SPECIALTY] === undefined;
  const [onboardInfo, setOnboardInfo] = useState<OnboardInfo>({
    specialty: "",
    specialtyInput: "",
  });
  const [selectASpecialtyText, setSelectASpecialtyText] = useState<string>("");
  const handleSetOnboardInfo = (info: OnboardInfo) => {
    setOnboardInfo(info);
  };
  const handleSubmitOnboardInfo = async () => {
    if (!onboardInfo.specialty) return;
    if (
      onboardInfo.specialty === SPECIALTIES.OTHER &&
      !onboardInfo.specialtyInput
    )
      return;

    await updateUserMetadata(
      {
        [ClerkMetadataKeys.SPECIALTY]:
          onboardInfo.specialty !== SPECIALTIES.OTHER
            ? onboardInfo.specialty
            : onboardInfo.specialtyInput.toLowerCase().trim(),
      },
      "onboard",
      (await getToken({ template: "supabase" })) ?? ""
    );
    await user?.reload();
  };

  const onBoardingInfoIncomplete =
    getOnBoardInfo &&
    (onboardInfo.specialty === "" ||
      (onboardInfo.specialty === SPECIALTIES.OTHER &&
        onboardInfo.specialtyInput === ""));
  return (
    <Modal
      easyToClose={!onBoardingInfoIncomplete}
      maxWidth="sm:max-w-2xl"
      onClose={() => {
        if (onBoardingInfoIncomplete) return;
        setIsOpen(false);
        if (getOnBoardInfo) {
          void handleSubmitOnboardInfo();
        }
      }}
      isOpen={isOpen}
    >
      <div>
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          {cloneElement(icon, {
            className: "h-8 w-8 text-blue-600 animate-pulse",
            "aria-hidden": "true",
          })}
        </div>
        <div className="mt-6 text-center">
          <Dialog.Title
            as="h3"
            className="text-2xl font-semibold leading-6 text-gray-900"
          >
            {title}
          </Dialog.Title>
          <div className="mt-4">
            <p className="text-base text-gray-500">{description}</p>
          </div>
        </div>

        {getOnBoardInfo && (
          <OnboardingQuestions
            onboardInfo={onboardInfo}
            handleSetOnboardInfo={handleSetOnboardInfo}
          />
        )}

        {steps && (
          <div className="mt-12">
            <div className="grid grid-cols-3 gap-8">
              {steps?.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
                    <div className="text-blue-600">
                      {cloneElement(step.icon, { className: "h-6 w-6" })}
                    </div>
                  </div>
                  <h4 className="mt-2 font-medium text-gray-900">
                    {step.title}
                  </h4>
                  <p className="mt-1 text-sm text-gray-500">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-12 sm:mt-14 space-y-4">
        {selectASpecialtyText && onBoardingInfoIncomplete && (
          <p className="text-red-500 mx-auto">{selectASpecialtyText}</p>
        )}
        <VetRecButton
          type="button"
          className={`${
            onBoardingInfoIncomplete ? "opacity-50 cursor-not-allowed" : ""
          } inline-flex w-full justify-center items-center gap-x-2 rounded-md bg-blue-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 hover:bg-blue-500`}
          onClick={async () => {
            if (onBoardingInfoIncomplete) {
              setSelectASpecialtyText("Please select your specialty");
              return;
            }
            startTour();
            if (getOnBoardInfo) {
              await handleSubmitOnboardInfo();
            }
          }}
        >
          <span>{primaryButtonText}</span>
          <span className="text-lg">🚀</span>
        </VetRecButton>
        <VetRecButton
          type="button"
          className={`${
            onBoardingInfoIncomplete ? "opacity-50 cursor-not-allowed" : ""
          } inline-flex w-full justify-center rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50`}
          onClick={async () => {
            if (onBoardingInfoIncomplete) {
              setSelectASpecialtyText("Please select your specialty");
              return;
            }
            onSkip();
            setIsOpen(false);
            if (getOnBoardInfo) {
              await handleSubmitOnboardInfo();
            }
          }}
        >
          {skipButtonText}
        </VetRecButton>
      </div>
    </Modal>
  );
};

export default WelcomeScreen;
